export interface PooledVideo {
  element: HTMLVideoElement;
  src: string;
  lastUsed: number;
  inUse: boolean;
}

export interface VideoPoolMetrics {
  poolSize: number;
  inUse: number;
  cacheHits: number;
  cacheMisses: number;
  totalAcquired: number;
  totalReleased: number;
  memoryEstimate: number;
}

export class VideoPool {
  private pool: PooledVideo[] = [];
  // Elements handed out while every pooled element was in use
  private untracked = new Set<HTMLVideoElement>();
  private maxPoolSize = 5;
  // Average memory estimate per loaded video in bytes
  private static readonly ESTIMATED_VIDEO_MEMORY_BYTES = 50 * 1024 * 1024; // 50MB
  private metrics: VideoPoolMetrics = {
    poolSize: 0,
    inUse: 0,
    cacheHits: 0,
    cacheMisses: 0,
    totalAcquired: 0,
    totalReleased: 0,
    memoryEstimate: 0,
  };

  constructor(maxSize?: number) {
    if (maxSize) {
      this.maxPoolSize = maxSize;
    }

    // HMR cleanup handler
    if (import.meta.hot) {
      import.meta.hot.dispose(() => {
        this.clear();
      });
    }
  }

  acquire(src: string): HTMLVideoElement {
    this.metrics.totalAcquired++;

    // Check if we already have this video in the pool
    const existingVideo = this.pool.find((v) => v.src === src && !v.inUse);
    if (existingVideo) {
      existingVideo.inUse = true;
      existingVideo.lastUsed = Date.now();
      this.metrics.cacheHits++;
      this.metrics.inUse++;
      this.updateMetrics();
      return existingVideo.element;
    }

    // Try to reuse an unused video element
    const unusedVideo = this.pool.find((v) => !v.inUse);
    if (unusedVideo) {
      // Reuse the element with new source
      unusedVideo.src = src;
      unusedVideo.element.src = src;
      unusedVideo.inUse = true;
      unusedVideo.lastUsed = Date.now();
      this.metrics.cacheMisses++;
      this.metrics.inUse++;
      this.updateMetrics();
      return unusedVideo.element;
    }

    // Create a new video element if we haven't reached the limit
    if (this.pool.length < this.maxPoolSize) {
      const video = document.createElement('video');
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      video.src = src;

      const pooledVideo: PooledVideo = {
        element: video,
        src,
        lastUsed: Date.now(),
        inUse: true,
      };

      this.pool.push(pooledVideo);
      this.metrics.cacheMisses++;
      this.metrics.inUse++;
      this.metrics.poolSize = this.pool.length;
      this.updateMetrics();
      return video;
    }

    // Pool is full and every element is in use: hand out an untracked element
    const video = document.createElement('video');
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.src = src;
    this.untracked.add(video);
    return video;
  }

  release(element: HTMLVideoElement): void {
    if (this.untracked.delete(element)) {
      VideoPool.unload(element);
      return;
    }

    const video = this.pool.find((v) => v.element === element && v.inUse);
    if (video) {
      this.metrics.totalReleased++;
      video.inUse = false;
      video.lastUsed = Date.now();
      this.metrics.inUse--;

      // Pause the video but keep it loaded for potential reuse
      video.element.pause();
      video.element.currentTime = 0;
      this.updateMetrics();
    }
  }

  preload(src: string): void {
    // Check if already in pool
    const existing = this.pool.find((v) => v.src === src);
    if (existing) return;

    // Only preload if we have room
    if (this.pool.length < this.maxPoolSize) {
      const video = document.createElement('video');
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.src = src;

      const pooledVideo: PooledVideo = {
        element: video,
        src,
        lastUsed: Date.now(),
        inUse: false,
      };

      this.pool.push(pooledVideo);
      this.metrics.poolSize = this.pool.length;
      this.updateMetrics();

      // Start loading metadata
      video.load();
    }
  }

  clear(): void {
    // Clean up all video elements
    this.pool.forEach((video) => VideoPool.unload(video.element));
    this.untracked.forEach(VideoPool.unload);
    this.pool = [];
    this.untracked.clear();
    this.metrics = {
      poolSize: 0,
      inUse: 0,
      cacheHits: 0,
      cacheMisses: 0,
      totalAcquired: 0,
      totalReleased: 0,
      memoryEstimate: 0,
    };
  }

  // Stop playback, free the media resource and remove the element from the DOM
  private static unload(element: HTMLVideoElement): void {
    element.pause();
    element.removeAttribute('src');
    element.load();
    element.remove();
  }

  getMetrics(): VideoPoolMetrics {
    this.metrics.poolSize = this.pool.length;
    return { ...this.metrics };
  }

  private updateMetrics(): void {
    // Estimate memory usage (very rough approximation)
    const loadedVideos = this.pool.filter((v) => v.element.src).length;
    this.metrics.memoryEstimate = loadedVideos * VideoPool.ESTIMATED_VIDEO_MEMORY_BYTES;
  }

  getCacheHitRate(): number {
    const total = this.metrics.cacheHits + this.metrics.cacheMisses;
    return total > 0 ? this.metrics.cacheHits / total : 0;
  }

  getPoolStatus(): string {
    const hitRate = (this.getCacheHitRate() * 100).toFixed(1);
    return `Pool: ${this.metrics.inUse}/${this.pool.length} in use, Cache hit rate: ${hitRate}%`;
  }
}

export default VideoPool;
